import { joiResolver } from '@hookform/resolvers/joi';
import { useForm, UseFormReturn } from 'react-hook-form';
import * as Joi from 'joi';
import dayjs from 'dayjs';
import { useState } from 'react';
import { MutationStatus } from 'react-query';
import { useGetRegistrationDocument } from '../../../api-refactored/candidate/useGetRegistrantionReportDocument';
import { FileUploadState } from '../../../models/File';
import { usePostGenerateReport } from '../../../api-refactored/ops-admin-access-arrangement/usePostGenerateReport';
import {SearchDate} from "../../../models";

interface EditDeleteReturn {
  /**
   * Perform submit action, either to call block call or unblock call, depends on the account status
   */
  submitGenerate: (submitData: any) => void;
  /**
   * Form to send edit & delete
   */
  form: UseFormReturn<SearchDate>;
  /**
   * React query mutation object
   */
  generateReportMutation: any;

  getDocumentUploadStatus: any;

  fileDownloadStatus: FileUploadState;
  onDialogClose: () => void;
  dialogState: boolean;
  process: MutationStatus;
}

/**
 * A hook to handle search functionality
 * @returns
 */
export const useAccessArrangementReport = (): EditDeleteReturn => {
  const [fileUUID, setFileUUID] = useState('');
  const [process, setProcess] = useState<MutationStatus>('idle');
  const [fileDownloadStatus, setFileDownloadStatus] = useState<FileUploadState>(
    FileUploadState.IDLE
  );
  const [dialogState, setDialogState] = useState<boolean>(false);

  const form = useForm<SearchDate>({
    mode: 'onSubmit',
    resolver: joiResolver(schema),
  });

  const getDocumentUploadStatus = useGetRegistrationDocument({
    fileKey: fileUUID || '',
    enabled: !!fileUUID && process === 'loading',
    refetchInterval: 5000,
    onSuccess: (res) => {
      setDialogState(true);
      setFileDownloadStatus(res.fileStatus);

      if (res.fileStatus === FileUploadState.FAILED || res.fileStatus === FileUploadState.TIMEOUT) {
        setProcess('error');
        setFileUUID('');
      }

      if (res.fileStatus === FileUploadState.UPLOADING) {
        setProcess('loading');
      }

      if (res.fileStatus === FileUploadState.SUCCESS) {
        setProcess('success');

        const newWindow = window.open(res.url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
          newWindow.opener = null;
        }
        setFileUUID('');
        setDialogState(false);
      }
    },
    onError: () => {
      setDialogState(false);
    },
  });

  const generateReportMutation = usePostGenerateReport({
    onSuccess: (res) => {
      setFileUUID(res.uuid);
    },
    onError: (_) => null,
  });

  const submitGenerate = (submitData: any) => {
    setProcess('loading');
    generateReportMutation.mutate({
      startDate: dayjs(submitData.startDate).format('YYYY-MM-DDT00:00:00'),
      endDate: dayjs(submitData.endDate).format('YYYY-MM-DDT23:59:59'),
    });
  };

  const onDialogClose = () => {
    setDialogState(false);
  };

  return {
    form,
    submitGenerate,
    generateReportMutation,
    getDocumentUploadStatus,
    fileDownloadStatus,
    onDialogClose,
    dialogState,
    process,
  };
};

const schema = Joi.object({
  startDate: Joi.string().required().messages({
    'date.base': 'Application Start Date not allowed to be empty.',
    'date.max': 'Application Start Date should not be greater than current date.',
    'string.empty': 'Application Start Date not allowed to be empty.',
    'any.required': 'Application Start Date not allowed to be empty.',
  }),
  endDate: Joi.string().required().messages({
    'date.base': 'Application End Date not allowed to be empty.',
    'date.max': 'Application End Date should not be greater than current date.',
    'string.empty': 'Application End Date not allowed to be empty.',
    'any.required': 'Application End Date not allowed to be empty.',
  }),
});
